import { useState, useEffect } from 'react';
import { useCollaborationStore } from '@/stores/collaborationStore';
import { useMapStore } from '@/stores/mapStore';
import type { RemoteUserState } from '@/types/collaboration';

export function UserCursors() {
  const { remoteUsers } = useCollaborationStore();
  const map = useMapStore((s) => s.map);
  const [interacting, setInteracting] = useState(false);

  // Hide cursors during any map interaction
  useEffect(() => {
    if (!map) return;

    const onStart = () => setInteracting(true);
    const onEnd = () => setInteracting(false);

    map.on('movestart', onStart);
    map.on('moveend', onEnd);
    return () => {
      map.off('movestart', onStart);
      map.off('moveend', onEnd);
    };
  }, [map]);

  // Filter users with valid cursor positions (need geo coords)
  const usersWithCursors = remoteUsers.filter(
    (user) => user.cursor && user.cursor.longitude != null && user.cursor.latitude != null
  );

  if (usersWithCursors.length === 0 || !map) return null;

  return (
    <div
      className="absolute inset-0 pointer-events-none overflow-hidden z-50"
      style={{
        opacity: interacting ? 0 : 1,
        transition: interacting ? 'opacity 0.15s ease-out' : 'opacity 0.4s ease-in',
      }}
    >
      {usersWithCursors.map((user) => (
        <UserCursor key={user.clientId} user={user} map={map} />
      ))}
    </div>
  );
}

interface UserCursorProps {
  user: RemoteUserState;
  map: maplibregl.Map;
}

function UserCursor({ user, map }: UserCursorProps) {
  if (!user.cursor) return null;

  // Project the remote user's geo coordinates to our local screen position
  const point = map.project([user.cursor.longitude, user.cursor.latitude]);

  return (
    <div
      className="user-cursor"
      style={{
        left: point.x,
        top: point.y,
        transition: 'left 0.1s ease-out, top 0.1s ease-out',
      }}
    >
      {/* Cursor pointer SVG */}
      <svg
        className="absolute -left-0.5 -top-0.5"
        width="16"
        height="16"
        viewBox="0 0 16 16"
        fill="none"
      >
        <path
          d="M1 1L1 11L4.5 7.5L7.5 14L9 13.5L6 6.5L11 6.5L1 1Z"
          fill={user.user.color}
          stroke="white"
          strokeWidth="1"
        />
      </svg>

      {/* User label */}
      <div
        className="user-cursor-label"
        style={{ backgroundColor: user.user.color }}
      >
        {user.user.name}
        {user.activeTool && (
          <span className="ml-1 opacity-75">({user.activeTool})</span>
        )}
      </div>
    </div>
  );
}
